import React, { useState, useMemo } from "react";
import {
  useGetBiActivityTrendsQuery,
  useGetBiUserActivityQuery,
  useGetBiStatsQuery,
  useGetBiFilterOptionsQuery,
} from "../../app/api/console/consoleBiApiSlice";
import ActivityTrendsChart from "../../components/BI/ActivityTrendsChart";
import StatsCards from "../../components/BI/StatsCards";
import UserActivityTable from "../../components/BI/UserActivityTable";
import MultiSelectDropdown from "../../components/common/MultiSelectDropdown";

const DEFAULT_FILTERS = {
  startDate: new Date(Date.now() - 5 * 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
  endDate: new Date().toISOString().split("T")[0],
  brands: [],
  users: [],
  sources: [],
  limit: 1000,
  offset: 0,
};

// Session sources returned by /bi/user-activity (see backend AGENTS.md "BI dashboard").
const SOURCE_OPTIONS = [
  { value: "hub_v2", label: "Hub" },
  { value: "hub_v1", label: "Hub V1" },
  { value: "login", label: "Login only" },
];

const inputClass =
  "w-full px-3 py-2 text-body-sm text-text-primary bg-white rounded-xl border border-border-subtle focus:outline-none focus:ring-2 focus:ring-accent-purple/20 focus:border-accent-purple/40 transition-all";

function BiDashboard() {
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [draft, setDraft] = useState({ startDate: DEFAULT_FILTERS.startDate, endDate: DEFAULT_FILTERS.endDate, brands: [], users: [], sources: [] });
  const [useProd] = useState(true);

  const hasChanges = draft.startDate !== appliedFilters.startDate ||
    draft.endDate !== appliedFilters.endDate ||
    JSON.stringify(draft.brands) !== JSON.stringify(appliedFilters.brands) ||
    JSON.stringify(draft.users) !== JSON.stringify(appliedFilters.users) ||
    JSON.stringify(draft.sources) !== JSON.stringify(appliedFilters.sources);

  const applyFilters = () => {
    setAppliedFilters((prev) => ({ ...prev, ...draft, offset: 0 }));
  };

  const { data: statsData, isLoading: statsLoading, error: statsError } = useGetBiStatsQuery({ useProd });
  const { data: filterOptionsData } = useGetBiFilterOptionsQuery({ useProd });

  const brandOptions = useMemo(() => {
    if (!filterOptionsData?.brands) return [];
    return filterOptionsData.brands.filter((b) => b !== "All");
  }, [filterOptionsData]);

  const userOptions = useMemo(() => {
    if (!filterOptionsData?.users) return [];
    return filterOptionsData.users.filter((u) => u !== "All");
  }, [filterOptionsData]);

  // Named brand sets from the backend (e.g. "All paying customers"); selecting one
  // selects its brands, so the trends query stays a plain brand list.
  const brandGroups = useMemo(
    () => Object.entries(filterOptionsData?.brand_groups || {}).map(([label, items]) => ({ label, items })),
    [filterOptionsData]
  );

  const activeBrands = appliedFilters.brands.length > 0 && appliedFilters.brands.length < brandOptions.length ? appliedFilters.brands : [];
  const activeUsers = appliedFilters.users.length > 0 && appliedFilters.users.length < userOptions.length ? appliedFilters.users : [];

  const {
    data: trendsData,
    isLoading: trendsLoading,
    error: trendsError,
  } = useGetBiActivityTrendsQuery({
    days: 90,
    startDate: appliedFilters.startDate,
    endDate: appliedFilters.endDate,
    brands: activeBrands,
    users: activeUsers,
    useProd,
  });

  const {
    data: userActivityData,
    isLoading: userActivityLoading,
    error: userActivityError,
  } = useGetBiUserActivityQuery({
    startDate: appliedFilters.startDate,
    endDate: appliedFilters.endDate,
    brands: activeBrands,
    users: activeUsers,
    sources: appliedFilters.sources,
    limit: appliedFilters.limit,
    offset: appliedFilters.offset,
    useProd,
  });

  const toggleSource = (value) => {
    setDraft((d) => ({
      ...d,
      sources: d.sources.includes(value) ? d.sources.filter((v) => v !== value) : [...d.sources, value],
    }));
  };

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-h2 text-text-primary'>BI Dashboard</h1>
        <p className='text-body-sm text-text-secondary mt-1'>User engagement and activity analytics</p>
      </div>

      <StatsCards stats={statsData} trendsData={trendsData} isLoading={statsLoading} error={statsError} />

      <div className='bg-white rounded-2xl border border-border-subtle p-6'>
        <div className='flex items-center justify-between mb-5'>
          <h2 className='text-body-lg font-semibold text-text-primary'>Activity Trends</h2>
        </div>

        <div className='flex flex-wrap items-end gap-3 mb-6'>
          <div className='min-w-[140px]'>
            <label className='block text-body-xs font-medium text-text-secondary mb-1'>Start Date</label>
            <input
              type='date'
              value={draft.startDate || ""}
              onChange={(e) => setDraft((d) => ({ ...d, startDate: e.target.value }))}
              className={inputClass}
            />
          </div>
          <div className='min-w-[140px]'>
            <label className='block text-body-xs font-medium text-text-secondary mb-1'>End Date</label>
            <input
              type='date'
              value={draft.endDate || ""}
              onChange={(e) => setDraft((d) => ({ ...d, endDate: e.target.value }))}
              className={inputClass}
            />
          </div>
          <div className='min-w-[140px] flex items-end'>
            <button
              onClick={applyFilters}
              disabled={!hasChanges || trendsLoading || userActivityLoading}
              className='px-4 py-2 text-body-sm font-semibold text-white bg-button-primary rounded-xl hover:bg-button-primary-hover disabled:opacity-30 disabled:cursor-not-allowed transition-colors'>
              {trendsLoading || userActivityLoading ? "Loading..." : "Apply"}
            </button>
          </div>
          <MultiSelectDropdown
            label="Brand"
            options={brandOptions}
            groups={brandGroups}
            selected={draft.brands}
            onChange={(brands) => setDraft((d) => ({ ...d, brands }))}
            placeholder="All brands"
          />
          <MultiSelectDropdown
            label="User"
            options={userOptions}
            selected={draft.users}
            onChange={(users) => setDraft((d) => ({ ...d, users }))}
            placeholder="All users"
          />
          <div>
            <label className='block text-body-xs font-medium text-text-secondary mb-1'>Session source</label>
            <div className='flex items-center gap-1'>
              {SOURCE_OPTIONS.map((opt) => {
                const on = draft.sources.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    type='button'
                    onClick={() => toggleSource(opt.value)}
                    className={`px-3 py-2 text-body-xs font-medium rounded-xl border transition-colors ${
                      on
                        ? "bg-accent-purple/10 border-accent-purple/40 text-accent-purple"
                        : "bg-white border-border-subtle text-text-secondary hover:text-text-primary"
                    }`}>
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <ActivityTrendsChart data={trendsData} isLoading={trendsLoading} error={trendsError} />
      </div>

      <div className='bg-white rounded-2xl border border-border-subtle p-6'>
        <UserActivityTable
          data={userActivityData}
          isLoading={userActivityLoading}
          error={userActivityError}
          useProd={useProd}
        />
      </div>
    </div>
  );
}

export default BiDashboard;
